import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { telephonyApi } from '@/api/telephony';
import { queryKeys } from '@/lib/query-keys';
import { useTenantId } from '@/providers/TenantProvider';
import type { UUID } from '@/types';

export function useTelephonyConnections() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: queryKeys.telephony.connections(tenantId),
    queryFn: () => telephonyApi.listConnections(tenantId),
    enabled: !!tenantId,
  });
}

export function useTelephonyConnection(connectionId: UUID) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: queryKeys.telephony.connection(tenantId, connectionId),
    queryFn: () => telephonyApi.getConnection(tenantId, connectionId),
    enabled: !!tenantId && !!connectionId,
  });
}

export function useUpdateTelephonyConnection(connectionId: UUID) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name?: string; status?: string }) =>
      telephonyApi.updateConnection(tenantId, connectionId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.telephony.connection(tenantId, connectionId) });
      qc.invalidateQueries({ queryKey: queryKeys.telephony.connections(tenantId) });
    },
  });
}

export function useAvailableTwilioNumbers(connectionId: UUID | undefined) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['telephony', tenantId, connectionId, 'available-numbers'],
    queryFn: () => telephonyApi.listAvailableTwilioNumbers(tenantId, connectionId!),
    enabled: !!tenantId && !!connectionId,
    staleTime: 30_000,
  });
}

export function useTwilioConfig(connectionId: UUID) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: queryKeys.telephony.twilio(tenantId, connectionId),
    queryFn: () => telephonyApi.getTwilioConfig(tenantId, connectionId),
    enabled: !!tenantId && !!connectionId,
  });
}

export function useConnectConfig(connectionId: UUID) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: queryKeys.telephony.connect(tenantId, connectionId),
    queryFn: () => telephonyApi.getConnectConfig(tenantId, connectionId),
    enabled: !!tenantId && !!connectionId,
  });
}

export function usePhoneNumbers(connectionId?: UUID) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: queryKeys.telephony.numbers(tenantId, connectionId),
    queryFn: () => telephonyApi.listPhoneNumbers(tenantId, connectionId),
    enabled: !!tenantId,
  });
}

export function useSyncPhoneNumbers(connectionId: UUID) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => telephonyApi.syncPhoneNumbers(tenantId, connectionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.telephony.numbers(tenantId, connectionId) });
      qc.invalidateQueries({ queryKey: queryKeys.telephony.numbers(tenantId) });
      qc.invalidateQueries({ queryKey: ['telephony', tenantId, connectionId, 'available-numbers'] });
    },
  });
}

export function useCreatePhoneNumber(connectionId: UUID) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof telephonyApi.createPhoneNumber>[2]) =>
      telephonyApi.createPhoneNumber(tenantId, connectionId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.telephony.numbers(tenantId, connectionId) });
      qc.invalidateQueries({ queryKey: queryKeys.telephony.numbers(tenantId) });
      qc.invalidateQueries({ queryKey: queryKeys.telephony.webhooks(tenantId, connectionId) });
      qc.invalidateQueries({ queryKey: queryKeys.telephony.twilio(tenantId, connectionId) });
      qc.invalidateQueries({ queryKey: ['telephony', tenantId, connectionId, 'available-numbers'] });
    },
  });
}

function invalidatePhoneNumberQueries(
  qc: ReturnType<typeof useQueryClient>,
  tenantId: UUID,
  connectionId: UUID
) {
  qc.invalidateQueries({ queryKey: queryKeys.telephony.numbers(tenantId, connectionId) });
  qc.invalidateQueries({ queryKey: queryKeys.telephony.numbers(tenantId) });
  qc.invalidateQueries({ queryKey: queryKeys.telephony.webhooks(tenantId, connectionId) });
  qc.invalidateQueries({ queryKey: queryKeys.telephony.twilio(tenantId, connectionId) });
  qc.invalidateQueries({ queryKey: ['telephony', tenantId, connectionId, 'available-numbers'] });
}

export function useUpdatePhoneNumber(connectionId: UUID) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      phoneNumberId,
      input,
    }: {
      phoneNumberId: UUID;
      input: Parameters<typeof telephonyApi.updatePhoneNumber>[3];
    }) => telephonyApi.updatePhoneNumber(tenantId, connectionId, phoneNumberId, input),
    onSuccess: () => invalidatePhoneNumberQueries(qc, tenantId, connectionId),
  });
}

export function useDeletePhoneNumber(connectionId: UUID) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (phoneNumberId: UUID) =>
      telephonyApi.deletePhoneNumber(tenantId, connectionId, phoneNumberId),
    onSuccess: () => invalidatePhoneNumberQueries(qc, tenantId, connectionId),
  });
}

export function useClearUnassignedPhoneNumbers(connectionId: UUID) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => telephonyApi.clearUnassignedPhoneNumbers(tenantId, connectionId),
    onSuccess: () => invalidatePhoneNumberQueries(qc, tenantId, connectionId),
  });
}

export function useConnectionWebhooks(connectionId: UUID) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: queryKeys.telephony.webhooks(tenantId, connectionId),
    queryFn: () => telephonyApi.getWebhooks(tenantId, connectionId),
    enabled: !!tenantId && !!connectionId,
  });
}

export function useCreateTelephonyConnection() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof telephonyApi.createConnection>[1]) =>
      telephonyApi.createConnection(tenantId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.telephony.connections(tenantId) });
      qc.invalidateQueries({ queryKey: queryKeys.telephony.defaults(tenantId) });
    },
  });
}

export function useTelephonyDefaults() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: queryKeys.telephony.defaults(tenantId),
    queryFn: () => telephonyApi.getDefaults(tenantId),
    enabled: !!tenantId,
  });
}

export function useUpdateTelephonyDefaults() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof telephonyApi.updateDefaults>[1]) =>
      telephonyApi.updateDefaults(tenantId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.telephony.defaults(tenantId) });
      qc.invalidateQueries({ queryKey: queryKeys.telephony.connections(tenantId) });
    },
  });
}

export function useUpdateTwilioConfig(connectionId: UUID) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof telephonyApi.updateTwilioConfig>[2]) =>
      telephonyApi.updateTwilioConfig(tenantId, connectionId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.telephony.twilio(tenantId, connectionId) });
      qc.invalidateQueries({ queryKey: queryKeys.telephony.webhooks(tenantId, connectionId) });
      qc.invalidateQueries({ queryKey: queryKeys.telephony.numbers(tenantId, connectionId) });
    },
  });
}

export function useUpdateConnectConfig(connectionId: UUID) {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof telephonyApi.updateConnectConfig>[2]) =>
      telephonyApi.updateConnectConfig(tenantId, connectionId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.telephony.connect(tenantId, connectionId) });
      qc.invalidateQueries({ queryKey: queryKeys.telephony.webhooks(tenantId, connectionId) });
    },
  });
}
